import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLocalizedNavigate, LanguageSwitcher } from "./LocalizedLink";
import { useLanguage } from "../i18n/LanguageProvider";
import { stripLang, withLang } from "../i18n/paths";

const navLinks = [
  { key: "about", label: "About", href: "#about", type: "scroll" },
  { key: "experience", label: "Experience", href: "#experience", type: "scroll" },
  { key: "articles", label: "Articles", href: "/articles", type: "route" },
  { key: "projects", label: "Projects", href: "/projects", type: "route" },
  { key: "aiResources", label: "AI Resources", href: "/ai-resources", type: "route" },
  { key: "contact", label: "Contact", href: "#contact", type: "scroll" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { t, lang } = useLanguage();
  // Compare against the path without its language prefix, so "/tr" counts as home.
  const barePath = stripLang(location.pathname);
  // Route links carry the localized href so opening one in a new tab, or
  // middle-clicking it, stays in the active language.
  const hrefFor = (link) => (link.type === "route" ? withLang(link.href, lang) : link.href);
  const testId = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = (e, link) => {
    e.preventDefault();
    setMobileOpen(false);

    if (link.type === "route") {
      navigate(link.href);
      return;
    }

    // If we're not on home, go home first then scroll
    if (barePath !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(link.href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }

    const el = document.querySelector(link.href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (barePath !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a
          href="/"
          data-testid="header-logo"
          onClick={handleLogoClick}
          className="text-white font-semibold text-lg tracking-tight hover:opacity-80 transition-opacity duration-200"
        >
          alperkirmizi
        </a>

        {/* Desktop */}
        <ul className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={hrefFor(link)}
                data-testid={`nav-link-${testId(link.label)}`}
                onClick={(e) => handleClick(e, link)}
                className={`px-4 py-2 text-sm transition-colors duration-200 rounded-full hover:bg-white/5 ${
                  link.type === "route" && barePath.startsWith(link.href)
                    ? "text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {t(`nav.${link.key}`, link.label)}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex md:items-center md:gap-1">
          <LanguageSwitcher />
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors duration-200"
        >
          {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/5 overflow-hidden"
          >
            <ul className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={hrefFor(link)}
                    data-testid={`mobile-nav-${testId(link.label)}`}
                    onClick={(e) => handleClick(e, link)}
                    className={`block px-4 py-3 text-sm transition-colors duration-200 rounded-xl hover:bg-white/5 ${
                      link.type === "route" && barePath.startsWith(link.href)
                        ? "text-white"
                        : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {t(`nav.${link.key}`, link.label)}
                  </a>
                </li>
              ))}
              <li className="mt-2 border-t border-white/5 pt-3">
                <LanguageSwitcher compact className="px-2" />
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
